
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { User } from '@/types/user';
import { generateUniqueNickname } from '@/utils/nicknameGenerator';
import OAuthButtons from './OAuthButtons';
import LoginDivider from './LoginDivider';
import EmailField from './fields/EmailField';
import PasswordField from './fields/PasswordField';
import NameField from './fields/NameField';
import { Form } from "@/components/ui/form";
import { Check } from 'lucide-react';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

// Define validation schema
const signupSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

type SignupFormValues = z.infer<typeof signupSchema>;

interface SignupFormProps {
  onSignup: (userData: User, inviteCode?: string) => void;
  onSwitchToLogin: () => void;
  inviteCode: string;
}

const SignupForm: React.FC<SignupFormProps> = ({ 
  onSignup, 
  onSwitchToLogin,
  inviteCode
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Initialize react-hook-form
  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
    },
  });

  const handleSubmit = async (values: SignupFormValues) => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Generate a unique nickname
      const nickname = generateUniqueNickname();
      
      // Mock signup (in a real app, this would be a call to an auth API)
      onSignup({
        id: 'user_' + Math.random().toString(36).substring(2, 15),
        name: values.name,
        email: values.email,
        nickname,
        avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
        isAdmin: values.email.includes('admin'), // Simple mock for admin detection
        hasCompletedOnboarding: false,
        dateJoined: new Date(),
        lastLogin: new Date()
      }, inviteCode);
      
      navigate('/onboarding');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderInviteCodeBadge = () => (
    <div className="mt-2 bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm flex items-center justify-center">
      <Check size={16} className="mr-1" />
      Invite code accepted!
    </div>
  );

  const renderFormHeader = () => (
    <div className="text-center">
      <h1 className="text-3xl font-display font-semibold text-hormony-charcoal">
        Create an Account
      </h1>
      <p className="mt-2 text-hormony-charcoal/70">
        Join us on your path to hormonal health
      </p>
      
      {renderInviteCodeBadge()}
    </div>
  );

  const renderSignupForm = () => (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="mt-8 space-y-6">
        <NameField form={form} />
        <EmailField form={form} />
        <PasswordField 
          form={form} 
          autoComplete="new-password"
          placeholder="Create a password"
        />
        
        <div>
          <Button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center"
          >
            {isLoading && <LoadingSpinner className="mr-2" />}
            Create Account
          </Button>
        </div>
        
        <div className="text-center mt-4">
          <button
            type="button"
            className="text-sm text-hormony-charcoal/70 hover:text-hormony-coral transition-colors"
            onClick={onSwitchToLogin}
          >
            Already have an account? Sign in
          </button>
        </div>
      </form>
    </Form>
  );

  return (
    <>
      {renderFormHeader()}
      {renderSignupForm()}
      <LoginDivider />
      <OAuthButtons isLoading={isLoading} setIsLoading={setIsLoading} />
    </>
  );
};

export default SignupForm;
